import type { FilesCategory } from '@brighterly/lib-core-types/files/enums'
import type { Lists } from '@brighterly/lib-core-types/lists/interfaces'
import type { Subscription } from '@brighterly/lib-core-types/lists/subscriptions/interfaces'
import type { ListsStore } from './interfaces'

import { LearnWayReason, KeepLearningReason } from '@brighterly/lib-core-types/subscription/enums'

import { isFullArray } from '@shared/lib/collections'
import { filterObjectByKeys } from '@shared/lib/collections'

const getCancellationRequestType = (state: ListsStore): Subscription['cancellation_request_type'] | null => {
  return state.lists?.subscription?.cancellation_request_type || null
}
const getCancellationRequestSubType = (state: ListsStore): Subscription['cancellation_request_sub_type'] | null => {
  return state.lists?.subscription?.cancellation_request_sub_type || null
}
const getPauseDurations = (state: ListsStore): Subscription['pause_durations'] | null => {
  return state.lists?.subscription?.pause_durations || null
}
const getPauseReasons = (state: ListsStore): Subscription['pause_reason'] | null => {
  return state.lists?.subscription?.pause_reason || null
}

const getLearnWaySubType = (state: ListsStore): Record<LearnWayReason, string> | null => {
  const subType = getCancellationRequestSubType(state)
  const reasons = [LearnWayReason.DecreasePace, LearnWayReason.ChangeSchedule]
  return !subType
    ? null
    : filterObjectByKeys(subType, reasons)
}

const getKeepLearningSubType = (state: ListsStore): Record<KeepLearningReason, string> | null => {
  const subType = getCancellationRequestSubType(state)
  const reasons = [KeepLearningReason.DecreasePace, KeepLearningReason.Discount]
  return !subType
    ? null
    : filterObjectByKeys(subType, reasons)
}

const getListFileCategory = (state: ListsStore): Record<FilesCategory, string> | null => state.lists?.file?.category || null

export const getters = {
  hasData: (state: ListsStore): boolean => !!state.lists,
  getDiscipline: (state: ListsStore): Lists['discipline'] | null => state.lists?.discipline || null,
  hasTimeSlots: (state: ListsStore): boolean => isFullArray(state.timeSlots),
  isTimeSlotsLoading: (state: ListsStore): ListsStore['timeSlotsLoading'] => state.timeSlotsLoading,
  getTimeSlots: (state: ListsStore): ListsStore['timeSlots'] => state.timeSlots,
  getOffer: (state: ListsStore): ListsStore['offer'] => state.offer,
  getListFileCategory: (state: ListsStore): Record<FilesCategory, string> | null => getListFileCategory(state),
  isListFileCategory: (state: ListsStore): boolean => !!getListFileCategory(state),
  getCancellationRequestType: (state: ListsStore): Subscription['cancellation_request_type'] | null => getCancellationRequestType(state),
  isCancellationRequestType: (state: ListsStore): boolean => !!getCancellationRequestType(state),
  getCancellationRequestSubType: (state: ListsStore): Subscription['cancellation_request_sub_type'] | null => getCancellationRequestSubType(state),
  isCancellationRequestSubType: (state: ListsStore): boolean => !!getCancellationRequestSubType(state),
  getLearnWaySubType: (state: ListsStore): Record<LearnWayReason, string> | null => getLearnWaySubType(state),
  isLearnWaySubType: (state: ListsStore): boolean => !!getLearnWaySubType(state),
  getKeepLearningSubType: (state: ListsStore): Record<KeepLearningReason, string> | null => getKeepLearningSubType(state),
  isKeepLearningSubType: (state: ListsStore): boolean => !!getKeepLearningSubType(state),
  getPauseDurations: (state: ListsStore): Subscription['pause_durations'] | null => getPauseDurations(state),
  getPauseReasons: (state: ListsStore): Subscription['pause_reason'] | null => getPauseReasons(state),
}
